import { Component, EventEmitter, OnInit, Input, Output, Inject, Optional } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Client, CommandStatus, FirmwareUpdateCommandDataOut, FirmwareUpdateGroupDataOut, FirmwareUpdateGroupForMetersQuery, FirmwareUpdateStartDataIn, GuidDictionaryItem, MeterDataOut } from '../../../../api/api-reference';
import { toUtcDate } from '../../../../helper/date-utils';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { FormsModule } from '@angular/forms';
import { DatePickerComponent } from '../../../core/components/date-picker/date-picker.component';
import { TimePickerComponent } from '../../../core/components/time-picker/time-picker.component';

interface ScheduledFirmwareItem {
  id: number;
  scheduledLogId: string;
  assetType: string;
  meterNo: string;
  meterType: string;
  status: 'pending' | 'failed' | 'success';
}


@Component({
  selector: 'app-firmware-update-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, AngularSvgIconModule, FormsModule, DatePickerComponent, TimePickerComponent],
  providers: [Client],
  templateUrl: './firmware-update-modal.component.html',
  styleUrl: './firmware-update-modal.component.scss'
})
export class FirmwareUpdateModalComponent implements OnInit {
  @Output() closeSignal = new EventEmitter<boolean>();
  @Output() meterDeselected = new EventEmitter<string>();
  @Input() metersIdsInput: string[] | undefined;
  @Input() targetMeterModels: GuidDictionaryItem[] | undefined;
  @Input() selectedMeters: MeterDataOut[] | undefined;

  activeTab: 'update' | 'scheduled' = 'update';
  activeMeters: MeterDataOut[] = [];
  selectedId: string | undefined;
  maxRetries: number = 1;
  showSchedulePanel = false;
  scheduledDate: string = '';
  scheduledTime: string = '';
  scheduledGroups: FirmwareUpdateGroupDataOut[] = [];

  CommandStatus = CommandStatus;

  constructor(
    private toastr: ToastrService,
    private client: Client,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.activeMeters = [...(this.selectedMeters ?? [])];
    if (this.targetMeterModels) {
      this.autoSelectIfSingle();
    } else {
      this.getMeterModels();
    }
  }

  get safeTargetMeterModels(): GuidDictionaryItem[] {
    return this.targetMeterModels ?? [];
  }

  get canUpdate(): boolean {
    return !!this.selectedId && this.activeMeters.length > 0 && this.maxRetries >= 1 && this.maxRetries <= 10;
  }

  get canSchedule(): boolean {
    return this.canUpdate && !!this.scheduledDate && !!this.scheduledTime;
  }

  private autoSelectIfSingle() {
    if (this.targetMeterModels?.length === 1) {
      this.selectedId = this.targetMeterModels[0].value;
    }
  }

  getMeterModels() {
    const firstId = this.activeMeters[0]?.id ?? this.metersIdsInput?.[0];
    if (firstId) {
      this.client.getAllMeterModelForFwUpdateOptions(firstId).subscribe({
        next: (response) => {
          this.targetMeterModels = response ?? [];
          this.autoSelectIfSingle();
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    } else {
      this.targetMeterModels = [];
    }
  }

  deselectMeter(meter: MeterDataOut) {
    this.activeMeters = this.activeMeters.filter(m => m.id !== meter.id);
    this.meterDeselected.emit(meter.id!);
  }

  increment() {
    if (this.maxRetries < 10) this.maxRetries++;
  }

  decrement() {
    if (this.maxRetries > 1) this.maxRetries--;
  }

  clearRetries() {
    this.maxRetries = 0;
  }

  toggleModal() {
    this.closeSignal.emit();
  }

  getDatePrefix(scheduledTime: Date): string {
    const offsetMs = Number(localStorage.getItem('tz') ?? 0) * 60000;

    const nowInTz = new Date(Date.now() + offsetMs);
    const todayStart = Date.UTC(nowInTz.getUTCFullYear(), nowInTz.getUTCMonth(), nowInTz.getUTCDate());

    const scheduledInTz = new Date(new Date(scheduledTime).getTime() + offsetMs);
    const scheduledStart = Date.UTC(scheduledInTz.getUTCFullYear(), scheduledInTz.getUTCMonth(), scheduledInTz.getUTCDate());

    const diffDays = Math.round((scheduledStart - todayStart) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return this.translate.instant('SCHEDULE_PREFIX_TODAY') + ', ';
    if (diffDays === 1) return this.translate.instant('SCHEDULE_PREFIX_TOMORROW') + ', ';
    if (diffDays > 1) return this.translate.instant('SCHEDULE_PREFIX_IN_DAYS', { days: diffDays }) + ', ';
    return '';
  }

  selectScheduledTab() {
    this.activeTab = 'scheduled';
    this.showSchedulePanel = false;
    this.loadScheduledItems();
  }

  private loadScheduledItems(): void {
    const meterIds = [
      ...this.activeMeters.map(m => m.id!),
      ...(this.metersIdsInput ?? []),
    ].filter((id, i, arr) => arr.indexOf(id) === i);

    this.client.queryDeferredReminders(new FirmwareUpdateGroupForMetersQuery({ meterIds })).subscribe({
      next: (response) => {
        this.scheduledGroups = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      }
    });
  }

  continue() {
    this.sendFirmwareUpdate();
  }

  scheduleUpdate() {
    const deferredTaskTime = this.parseScheduledDateTime();
    this.sendFirmwareUpdate(deferredTaskTime);
  }

  get timezoneOffset(): string {
    const minutes = Number(localStorage.getItem('tz') ?? 0);
    const abs = Math.abs(minutes);
    const sign = minutes >= 0 ? '+' : '-';
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}${String(abs % 60).padStart(2, '0')}`;
  }

  private parseScheduledDateTime(): Date {
    const timeStr = this.scheduledTime.toLowerCase();
    const isPm = timeStr.includes('pm');
    const [hourStr, minuteStr] = timeStr.replace(/am|pm/, '').split(':');
    let hours = parseInt(hourStr, 10);
    const minutes = parseInt(minuteStr, 10);
    if (isPm && hours !== 12) hours += 12;
    if (!isPm && hours === 12) hours = 0;
    const [year, month, day] = this.scheduledDate.split('-').map(Number);
    return toUtcDate(new Date(year, month - 1, day, hours, minutes, 0));
  }

  private sendFirmwareUpdate(deferredTaskTime?: Date): void {
    const dataIn = new FirmwareUpdateStartDataIn();
    dataIn.meterIds = this.activeMeters.map(meter => meter.id!);
    dataIn.meterModelId = this.selectedId!;
    dataIn.maxRetryCount = this.maxRetries;
    dataIn.deferredTaskTime = deferredTaskTime;

    this.client.updateFirmwareImage(dataIn).subscribe({
      next: () => {
        this.toastr.success('Firmware update scheduled successfully.');
        this.showSchedulePanel = false;
        this.closeSignal.emit();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  removeBatch(group: FirmwareUpdateGroupDataOut) {
    this.client.deleteDeferredReminderBatch([group.id]).subscribe({
      next: () => {
        this.scheduledGroups = this.scheduledGroups.filter(g => g !== group);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  deleteItem(item: FirmwareUpdateCommandDataOut) {
    this.client.deleteDeferredReminder(item.id).subscribe({
      next: () => {
        for (const group of this.scheduledGroups) {
          group.commands = group.commands?.filter(i => i.id !== item.id) ?? [];
        }
        this.scheduledGroups = this.scheduledGroups.filter(g => (g.commands?.length ?? 0) > 0);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  retryItem(item: ScheduledFirmwareItem) {
    item.status = 'pending';
  }
}
